import { signal } from '@preact/signals';

/**
 * Arrival-state journey playback.
 *
 * `progress` is `null` when idle, otherwise a number in `[0, 1]` indicating
 * how far along the trail the visible head is. `state` is the human-meaningful
 * status — UI components branch on it to render Play/Pause/Resume/Replay.
 *
 * `LiveMap` reacts by slicing the trail's GeoJSON LineString to the
 * corresponding subset of waypoints. `StatsCard` reads the active waypoint
 * at the head to surface that moment's speed, battery, and timestamp.
 */

export type PlaybackState = 'idle' | 'playing' | 'paused' | 'completed';

export const playbackProgress = signal<number | null>(null);
export const playbackState = signal<PlaybackState>('idle');

const PLAYBACK_MS = 4_000;
const COMPLETED_HOLD_MS = 600;

let frame: number | null = null;
let segmentStartedAt = 0;
let segmentStartedFrom = 0;
let completedResetTimer: ReturnType<typeof setTimeout> | null = null;

function clearCompletedReset(): void {
  if (completedResetTimer) clearTimeout(completedResetTimer);
  completedResetTimer = null;
}

function tick(now: number): void {
  // Only the playing state advances — pause freezes here without scheduling
  // the next frame.
  if (playbackState.value !== 'playing') {
    frame = null;
    return;
  }
  const elapsed = now - segmentStartedAt;
  const progress = Math.min(1, segmentStartedFrom + elapsed / PLAYBACK_MS);
  playbackProgress.value = progress;
  if (progress < 1) {
    frame = requestAnimationFrame(tick);
    return;
  }
  // Completion — hold the "fully drawn" frame briefly so the watcher sees
  // the end state, then return to idle and restore the static full trail.
  frame = null;
  playbackState.value = 'completed';
  completedResetTimer = setTimeout(() => {
    if (playbackState.value === 'completed') {
      playbackProgress.value = null;
      playbackState.value = 'idle';
    }
  }, COMPLETED_HOLD_MS);
}

export function playJourney(): void {
  cancelPlayback();
  segmentStartedAt = performance.now();
  segmentStartedFrom = 0;
  playbackProgress.value = 0;
  playbackState.value = 'playing';
  frame = requestAnimationFrame(tick);
}

export function pausePlayback(): void {
  if (playbackState.value !== 'playing') return;
  if (frame !== null) cancelAnimationFrame(frame);
  frame = null;
  playbackState.value = 'paused';
}

export function resumePlayback(): void {
  if (playbackState.value !== 'paused') return;
  segmentStartedAt = performance.now();
  segmentStartedFrom = playbackProgress.value ?? 0;
  playbackState.value = 'playing';
  frame = requestAnimationFrame(tick);
}

export function cancelPlayback(): void {
  if (frame !== null) cancelAnimationFrame(frame);
  frame = null;
  clearCompletedReset();
  playbackProgress.value = null;
  playbackState.value = 'idle';
}
